import { useState, useEffect } from "react";
import { useParams, useLocation } from "@/lib/router-compat";
import { Play, Pause, Mic, Loader2 } from "lucide-react";
import { useArticleAudioPlayer } from "@/context/ArticleAudioPlayerContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import FullArticlePlayer from "./FullArticlePlayer";
import { useToast } from "@/hooks/use-toast";

const FloatingArticlePlayerBar = () => {
  const { id } = useParams();
  const location = useLocation();
  const { currentArticle, isPlaying, pause, resume, currentTime, duration, playArticle } = useArticleAudioPlayer();
  const isMobile = useIsMobile();
  const [isFullPlayerOpen, setIsFullPlayerOpen] = useState(false);
  const [pageArticle, setPageArticle] = useState<any>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();

  // Fetch the current page's article
  useEffect(() => {
    const fetchArticle = async () => {
      if (!id) return;
      
      const { data } = await supabase
        .from('articles')
        .select('id, title, source_name, image_url, audio_url')
        .eq('id', id)
        .maybeSingle();
      
      if (data) {
        setPageArticle(data);
      }
    };
    
    fetchArticle();
  }, [id]);

  // Don't show if no article
  if (!pageArticle) return null;
  
  // Check if this article is currently playing
  const isCurrentArticlePlaying = currentArticle?.id === pageArticle.id && isPlaying;

  const progress = currentArticle?.id === pageArticle.id && duration > 0 
    ? (currentTime / duration) * 100 
    : 0;

  const handlePlayPause = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // If no audio exists, generate it first
    if (!pageArticle.audio_url) {
      setIsGenerating(true);
      
      try {
        const { data, error } = await supabase.functions.invoke('generate-article-audio', {
          body: {
            articleId: pageArticle.id,
            regenerate: false
          }
        });

        if (error) throw error;

        // Update the local state with the new audio URL
        setPageArticle({ ...pageArticle, audio_url: data.audioUrl });
        
        // Start playing the newly generated audio
        playArticle({
          id: pageArticle.id,
          title: pageArticle.title,
          source: pageArticle.source_name,
          audioUrl: data.audioUrl,
          imageUrl: pageArticle.image_url
        });

        toast({
          title: "Audio generated",
          description: "Now playing article audio",
        });
      } catch (error) {
        console.error('Error generating audio:', error);
        toast({
          title: "Error",
          description: "Failed to generate audio. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsGenerating(false);
      }
      return;
    }
    
    // If this article is playing, pause/resume
    if (currentArticle?.id === pageArticle.id) {
      if (isPlaying) {
        pause();
      } else {
        resume();
      }
    } else {
      // Start playing this article
      playArticle({
        id: pageArticle.id,
        title: pageArticle.title,
        source: pageArticle.source_name,
        audioUrl: pageArticle.audio_url,
        imageUrl: pageArticle.image_url
      });
    }
  };

  return (
    <>
      <div
        onClick={() => setIsFullPlayerOpen(true)}
        className="w-full cursor-pointer"
      >
        {/* Thin progress bar at top */}
        <div className="h-0.5 bg-background">
          <div 
            className="h-full bg-byteline-green transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Player controls bar */}
        <div className="bg-background border-t border-border px-4 py-3 flex items-center gap-3">
          {/* Artwork thumbnail */}
          <div className="w-12 h-12 rounded-lg bg-muted flex-shrink-0 overflow-hidden">
            {pageArticle.image_url ? (
              <img 
                src={pageArticle.image_url} 
                alt={pageArticle.title}
                className="w-full h-full object-cover"
              loading="lazy" decoding="async" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-byteline-green/20 to-blue-500/20" />
            )}
          </div>

          {/* Article info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{pageArticle.title}</p>
            <p className="text-xs text-muted-foreground truncate">{pageArticle.source_name}</p>
          </div>

          {/* Play/Pause/Generate button */}
          <button
            onClick={handlePlayPause}
            disabled={isGenerating}
            className="w-10 h-10 rounded-full bg-byteline-green hover:bg-byteline-green/90 flex items-center justify-center flex-shrink-0 transition-colors disabled:opacity-50"
          >
            {isGenerating ? (
              <Loader2 className="h-5 w-5 text-white animate-spin" />
            ) : !pageArticle.audio_url ? (
              <Mic className="h-5 w-5 text-white" />
            ) : isCurrentArticlePlaying ? (
              <Pause className="h-5 w-5 text-white fill-white" />
            ) : (
              <Play className="h-5 w-5 text-white fill-white" />
            )}
          </button>
        </div>
      </div>

      <FullArticlePlayer 
        open={isFullPlayerOpen} 
        onOpenChange={setIsFullPlayerOpen}
      />
    </>
  );
};

export default FloatingArticlePlayerBar;
